import useUsers from "@/hooks/useUsers";
import useGlobalStore from "@/services/store/useGlobalStore";
import { formatTimestamp } from "@/utils/functions/date";
import { Box, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { FC } from "react";
import {
  AverageSpeedColumn,
  CurrentCoordinateColumn,
  GpsTypeColumn,
  GrammarDateColumn,
  LastMissionColumn,
  MissionDateColumn,
  MissionDurationColumn,
  MissionProgressColumn,
} from "./components";
import IsMissionOnGoingColumn from "./components/IsMissionOnGoingColumn";
import StatusColumn from "./components/StatusColumn";

interface StatusDisplayerProps {
  usersToShow?: UserWithColor[];
}

const StatusDisplayer: FC<StatusDisplayerProps> = ({ usersToShow }) => {
  // State handling
  const { stateByUserLabel } = useGlobalStore((state) => state);

  const { users } = useUsers();
  const filteredUsers = usersToShow ?? users;

  const getUserState = (user: UserWithColor) => stateByUserLabel[user.alias ?? user.username] ?? null;

  const lastRefreshDate = useGlobalStore((state) => state.lastStateRefreshDate);

  const getColumns = (): readonly GridColDef<UserWithState>[] => {
    return [
      {
        headerAlign: "center",
        sortable: false,
        width: 120,
        field: "state.smartphoneId",
        headerName: "Smartphone",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <Typography>{params.row.state?.alias}</Typography>
            </Stack>
          );
        },
      },

      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "status",
        headerName: "Statut du smartphone",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <StatusColumn status={params.row.status} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "state.maskStatus",
        headerName: "Statut du masque",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <StatusColumn status={params.row.state?.maskStatus} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "state.gpsStatus",
        headerName: "Statut du GPS",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <StatusColumn status={params.row.state?.gpsStatus} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "state.gpsType",
        headerName: "Type de GPS",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <GpsTypeColumn gpsType={params.row.state?.gpsType} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 120,
        field: "state.gpsPrecision",
        headerName: "Précision du GPS",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <Typography>{params.row.state?.gpsPrecision}</Typography>
            </Stack>
          );
        },
      },

      {
        headerAlign: "center",
        sortable: false,
        width: 120,
        field: "state.grammarName",
        headerName: "Grammaire",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <Typography>{params.row.state?.grammarName}</Typography>
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 180,
        field: "state.grammarReceiveDate",
        headerName: "Date réception grammaire",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <GrammarDateColumn date={params.row.state?.grammarReceiveDate} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "state.isMissionOnGoing",
        headerName: "Mission en cours ?",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <IsMissionOnGoingColumn isMissionOnGoing={!!params.row.state?.isMissionOngoing} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "state.lastMission",
        headerName: "Dernière mission",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <LastMissionColumn lastMission={params.row.state?.lastMission} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 180,
        field: "state.missionDate",
        headerName: "Date mission",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <MissionDateColumn missionDate={params.row.state?.missionDate} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 120,
        field: "state.missionDurationSeconds",
        headerName: "Durée mission",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <MissionDurationColumn duration={params.row.state?.missionDurationSeconds} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 120,
        field: "state.missionProgress",
        headerName: "Progression",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <MissionProgressColumn
                isMissionOngoing={!!params.row.state?.isMissionOngoing}
                currentProgress={params.row.state?.missionProgress?.currentProgress}
                totalProgress={params.row.state?.missionProgress?.totalProgress}
              />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 180,
        field: "state.currentCoordinate",
        headerName: "Position",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <CurrentCoordinateColumn coordinate={params.row?.state?.currentCoordinate} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 150,
        field: "state.averageSpeed",
        headerName: "Vitesse moyenne",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <AverageSpeedColumn averageSpeed={params.row?.state?.averageSpeed} />
            </Stack>
          );
        },
      },
      {
        headerAlign: "center",
        sortable: false,
        width: 90,
        field: "state.appVersion",
        headerName: "Version",
        renderCell: (params) => {
          return (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <Typography>{params.row.state?.appVersion}</Typography>
            </Stack>
          );
        },
      },
    ];
  };

  const columns = getColumns();

  const rows: UserWithState[] = filteredUsers.map((user) => ({ ...user, state: getUserState(user) }));

  return (
    <Box sx={{ padding: "10px", boxSizing: "border-box", maxHeight: "100%", overflowY: " auto" }}>
      <Stack direction="row" alignItems="center" sx={{ padding: "4px" }}>
        <Typography sx={{ padding: "10px" }}>
          Dernier rafraichissement : {lastRefreshDate ? formatTimestamp(lastRefreshDate.getTime()) : "Jamais"}
        </Typography>
      </Stack>

      <DataGrid hideFooter columns={columns} rows={rows} />
    </Box>
  );
};

export default StatusDisplayer;
